import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  clearMerchandisingDraft,
  loadMerchandisingDraft,
  saveMerchandisingDraft,
} from './draftStorage';
import {
  ADVANCED_CONDITIONS_CAPABILITY,
  ruleUsesAdvancedConditions,
  ruleUsesSearchTermActions,
  SEARCH_TERMS_CAPABILITY,
} from '../model/ruleCapabilities';

const idleRequest = () => ({
  status: 'idle',
  refreshing: false,
  error: null,
  requestId: null,
});

const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) =>
      `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

export const ruleFilterFingerprint = (filters) => {
  const { cursor: _cursor, ...stableFilters } = filters || {};
  return stableStringify(stableFilters);
};

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : ((random & 0x3) | 0x8)).toString(16);
  });
};

const getApi = (extra) => {
  const api = extra?.serviceRegistry?.merchandisingApi;
  if (!api) throw new Error('merchandisingApi not available on current transport');
  return api;
};

const getIndexingApi = (extra) => {
  const api = extra?.serviceRegistry?.indexingApi;
  if (!api) throw new Error('indexingApi not available on current transport');
  return api;
};

const COMPAT_FULL_JOB_PREFIX = 'compat-cloud-full';

const revisionAtLeast = (published, target) => {
  if (published === null || published === undefined) return false;
  const left = String(published);
  const right = String(target);
  if (left === right) return true;
  if (!/^\d+$/.test(left) || !/^\d+$/.test(right)) return false;
  const normalizedLeft = left.replace(/^0+(?=\d)/, '');
  const normalizedRight = right.replace(/^0+(?=\d)/, '');
  return normalizedLeft.length > normalizedRight.length
    || (normalizedLeft.length === normalizedRight.length && normalizedLeft > normalizedRight);
};

const compatibilityFullJobId = (shopId, revision) => (
  `${COMPAT_FULL_JOB_PREFIX}:${encodeURIComponent(shopId)}:${encodeURIComponent(revision)}`
);

const parseCompatibilityFullJobId = (jobId) => {
  const match = /^compat-cloud-full:([^:]+):([^:]+)$/.exec(String(jobId));
  if (!match) return null;
  try {
    return {
      shopId: decodeURIComponent(match[1]),
      targetRevision: decodeURIComponent(match[2]),
    };
  } catch (error) {
    return null;
  }
};

const errorPayload = (error) => ({
  code: error.code || 'MERCHANDISING_REQUEST_FAILED',
  message: error.message || 'Merchandising request failed.',
  details: error.details || [],
  requestId: error.requestId || null,
  currentRevision: error.currentRevision ?? null,
  recovery: error.recovery ?? null,
});

const scopeGeneration = (getState) =>
  Number(getState().merchandising?.scopeGeneration || 0);

const staleScopePayload = () => ({
  code: 'STALE_MERCHANDISING_SCOPE',
  message: 'The merchandising shop changed while the request was in progress.',
  details: [],
  requestId: null,
  currentRevision: null,
  recovery: null,
});

const fulfillInScope = (
  payload,
  generation,
  { getState, fulfillWithValue, rejectWithValue },
) => (
  scopeGeneration(getState) === generation
    ? fulfillWithValue(payload, { scopeGeneration: generation })
    : rejectWithValue(staleScopePayload(), {
      scopeGeneration: generation,
      staleScope: true,
    })
);

const rejectInScope = (
  payload,
  generation,
  { getState, rejectWithValue },
) => {
  const staleScope = scopeGeneration(getState) !== generation;
  return rejectWithValue(staleScope ? staleScopePayload() : payload, {
    scopeGeneration: generation,
    staleScope,
  });
};

const actionMatchesScope = (state, action) =>
  Number(action.meta.scopeGeneration) === Number(state.scopeGeneration);

const unsupportedSearchTermsPayload = () => ({
  code: 'MERCHANDISING_CAPABILITY_REQUIRED',
  message: 'Search phrase actions are not supported by this store integration.',
  details: [{ capability: SEARCH_TERMS_CAPABILITY }],
  requestId: null,
  currentRevision: null,
  recovery: null,
});

const unsupportedAdvancedConditionsPayload = () => ({
  code: 'MERCHANDISING_CAPABILITY_REQUIRED',
  message: 'Advanced merchandising conditions are not supported by this store integration.',
  details: [{ capability: ADVANCED_CONDITIONS_CAPABILITY }],
  requestId: null,
  currentRevision: null,
  recovery: null,
});

const compatibilityErrorForRule = (rule, state) => {
  const feature = state.merchandising;
  if (ruleUsesSearchTermActions(rule) && !feature?.capability?.searchTerms) {
    return unsupportedSearchTermsPayload();
  }
  if (!feature?.capability?.advancedConditions && ruleUsesAdvancedConditions(
    rule,
    feature?.discovery?.schema?.condition_fields,
  )) return unsupportedAdvancedConditionsPayload();
  return null;
};

export const fetchMerchandisingSchema = createAsyncThunk(
  'merchandising/fetchSchema',
  async ({ languageId = null }, { extra, rejectWithValue }) => {
    try {
      return await getApi(extra).getSchema(languageId);
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const fetchMerchandisingIndexState = createAsyncThunk(
  'merchandising/fetchIndexState',
  async ({ languageId = null }, { extra, rejectWithValue }) => {
    try {
      return await getApi(extra).getIndexState(languageId);
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const fetchMerchandisingRules = createAsyncThunk(
  'merchandising/fetchRules',
  async ({ filters, append = false }, { extra, rejectWithValue }) => {
    try {
      return {
        response: await getApi(extra).listRules(filters),
        filters,
        fingerprint: ruleFilterFingerprint(filters),
        append,
      };
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const fetchMerchandisingRule = createAsyncThunk(
  'merchandising/fetchRule',
  async ({ id }, { extra, rejectWithValue }) => {
    try {
      return await getApi(extra).getRule(String(id));
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const fetchMerchandisingQueryGroups = createAsyncThunk(
  'merchandising/fetchQueryGroups',
  async ({ languageId = null, cursor = null, limit = 100 } = {}, { extra, rejectWithValue }) => {
    try {
      return await getApi(extra).listQueryGroups({ languageId, cursor, limit });
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const fetchMerchandisingQueryGroup = createAsyncThunk(
  'merchandising/fetchQueryGroup',
  async ({ id, languageId = null }, { extra, rejectWithValue }) => {
    try {
      return await getApi(extra).getQueryGroup({ id: String(id), languageId });
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const exportMerchandisingCsv = createAsyncThunk(
  'merchandising/exportCsv',
  async (payload, { extra, rejectWithValue }) => {
    try {
      return await getApi(extra).exportCsv(payload);
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const previewMerchandisingCsv = createAsyncThunk(
  'merchandising/previewCsv',
  async (payload, { extra, rejectWithValue }) => {
    try {
      return await getApi(extra).previewCsv(payload);
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const importMerchandisingCsv = createAsyncThunk(
  'merchandising/importCsv',
  async (payload, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    try {
      const response = await getApi(extra).importCsv(payload);
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const saveMerchandisingQueryGroup = createAsyncThunk(
  'merchandising/saveQueryGroup',
  async (payload, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    try {
      const api = getApi(extra);
      const response = payload.id
        ? await api.updateQueryGroup(payload)
        : await api.createQueryGroup(payload);
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const changeMerchandisingQueryGroupStatus = createAsyncThunk(
  'merchandising/changeQueryGroupStatus',
  async (payload, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    try {
      const response = await getApi(extra).setQueryGroupStatus(payload);
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const deleteMerchandisingQueryGroup = createAsyncThunk(
  'merchandising/deleteQueryGroup',
  async (payload, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    try {
      const response = await getApi(extra).deleteQueryGroup(payload);
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const searchMerchandisingValues = createAsyncThunk(
  'merchandising/searchValues',
  async ({
    action, field = null, query = '', languageId = null, cursor = null, limit = 20,
  }, { extra, rejectWithValue }) => {
    try {
      const api = getApi(extra);
      return action === 'merchandising.category_values.search'
        ? await api.searchCategoryValues({
          query, languageId, cursor, limit,
        })
        : await api.searchConditionValues({
          field, query, languageId, cursor, limit,
        });
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const validateMerchandisingRule = createAsyncThunk(
  'merchandising/validateRule',
  async ({ rule }, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    const compatibilityError = compatibilityErrorForRule(rule, getState());
    if (compatibilityError) {
      return rejectInScope(compatibilityError, generation, { getState, rejectWithValue });
    }
    try {
      const response = await getApi(extra).validateRule(rule);
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const saveMerchandisingRule = createAsyncThunk(
  'merchandising/saveRule',
  async (
    {
      id = null, revision = null, rule, clientMutationId,
    },
    {
      extra, getState, fulfillWithValue, rejectWithValue,
    },
  ) => {
    const generation = scopeGeneration(getState);
    const draftScope = getState().merchandising.editor.scope;
    const compatibilityError = compatibilityErrorForRule(rule, getState());
    if (compatibilityError) {
      return rejectInScope(compatibilityError, generation, { getState, rejectWithValue });
    }
    try {
      const api = getApi(extra);
      const response = id
        ? await api.updateRule({ id: String(id), revision, rule })
        : await api.createRule({ clientMutationId, rule });
      if (draftScope) clearMerchandisingDraft(draftScope);
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const changeMerchandisingRuleStatus = createAsyncThunk(
  'merchandising/changeRuleStatus',
  async (
    {
      id, revision, status, rule = null,
    },
    {
      extra, getState, fulfillWithValue, rejectWithValue,
    },
  ) => {
    const generation = scopeGeneration(getState);
    const currentRule = rule || getState().merchandising.rules.entities[String(id)] || null;
    const compatibilityError = status === 'active' && currentRule
      ? compatibilityErrorForRule(currentRule, getState())
      : null;
    if (compatibilityError) {
      return rejectInScope(compatibilityError, generation, { getState, rejectWithValue });
    }
    try {
      const response = await getApi(extra).setRuleStatus({
        id: String(id), revision, status,
      });
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const archiveMerchandisingRule = createAsyncThunk(
  'merchandising/archiveRule',
  async (
    { id, revision },
    {
      extra, getState, fulfillWithValue, rejectWithValue,
    },
  ) => {
    const generation = scopeGeneration(getState);
    try {
      const response = await getApi(extra).archiveRule({ id: String(id), revision });
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const deleteMerchandisingRule = createAsyncThunk(
  'merchandising/deleteRule',
  async (
    { id, revision },
    {
      extra, getState, fulfillWithValue, rejectWithValue,
    },
  ) => {
    const generation = scopeGeneration(getState);
    try {
      const response = await getApi(extra).deleteRule({ id: String(id), revision });
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const fetchMerchandisingCategories = createAsyncThunk(
  'merchandising/fetchCategories',
  async ({ languageId = null, includeImages = true }, { extra, rejectWithValue }) => {
    try {
      return await getApi(extra).listCategories({ languageId, includeImages });
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
  {
    condition: (_, { getState }) => {
      const request = getState().merchandising?.categories?.request;
      return request?.status !== 'loading' && !request?.refreshing;
    },
  },
);

export const saveMerchandisingCategoryChanges = createAsyncThunk(
  'merchandising/saveCategoryChanges',
  async ({
    sets, clears, languageId = null,
  }, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    const api = getApi(extra);
    const result = {
      sets: null,
      clears: null,
      appliedSetIds: [],
      appliedClearIds: [],
      projection: null,
    };
    let canonicalClears = clears;
    const chunksOf = (items, size = 200) => {
      const chunks = [];
      for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
      }
      return chunks;
    };
    const reloadAndCompare = async () => {
      const projection = await api.listCategories({ languageId, includeImages: true });
      result.projection = projection;
      const byId = Object.fromEntries(projection.items.map((row) => [String(row.id), row]));
      sets.forEach((change) => {
        const row = byId[String(change.category_id)];
        if (row && Number(row.manual_multiplier) === Number(change.multiplier)
          && !result.appliedSetIds.includes(String(change.category_id))) {
          result.appliedSetIds.push(String(change.category_id));
        }
      });
      clears.forEach((change) => {
        const row = byId[String(change.category_id)];
        if (row) {
          if (row.manual_multiplier == null
            && !result.appliedClearIds.includes(String(change.category_id))) {
            result.appliedClearIds.push(String(change.category_id));
          }
        }
      });
    };
    try {
      // The mixed operation is deliberately non-atomic. Successful sets are
      // retained if the later clear call fails. Every bridge request remains
      // within the canonical 200-row batch limit.
      const setResponses = [];
      for (const chunk of chunksOf(sets)) {
        // eslint-disable-next-line no-await-in-loop
        setResponses.push(await api.saveManualCategories(chunk));
        result.appliedSetIds.push(...chunk.map((change) => String(change.category_id)));
      }
      result.sets = setResponses.length > 0 ? setResponses : null;

      // Requirement 61 deliberately puts a canonical read barrier between
      // the two endpoint families. It proves the sets before any clears are
      // submitted and refreshes revisions used by recovery.
      if (sets.length > 0 && clears.length > 0) {
        await reloadAndCompare();
        const byId = Object.fromEntries(
          result.projection.items.map((row) => [String(row.id), row]),
        );
        canonicalClears = clears.map((change) => {
          const row = byId[String(change.category_id)];
          return row ? {
            ...change,
            rule_id: row.manual_rule_id,
            revision: row.manual_rule_revision,
          } : change;
        });
      }

      const clearResponses = [];
      const remainingClears = canonicalClears.filter((change) =>
        !result.appliedClearIds.includes(String(change.category_id)));
      for (const chunk of chunksOf(remainingClears)) {
        // eslint-disable-next-line no-await-in-loop
        clearResponses.push(await api.clearManualCategories(chunk));
        result.appliedClearIds.push(...chunk.map((change) => String(change.category_id)));
      }
      result.clears = clearResponses.length > 0 ? clearResponses : null;
      return fulfillInScope(result, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      // Category mutations have no mutation ID. A timeout or conflict is
      // recovered only by reloading canonical projection and comparing the
      // requested values; never blindly resubmit an unknown batch.
      try {
        await reloadAndCompare();
      } catch (_) {
        // Preserve the original typed mutation error when recovery read fails.
      }
      return rejectInScope(
        { ...errorPayload(error), partialResult: result },
        generation,
        { getState, rejectWithValue },
      );
    }
  },
);

export const reviewMerchandisingCategoryAi = createAsyncThunk(
  'merchandising/reviewCategoryAi',
  async (
    payload,
    {
      extra, getState, fulfillWithValue, rejectWithValue,
    },
  ) => {
    const generation = scopeGeneration(getState);
    try {
      const response = await getApi(extra).reviewAiCategory(payload);
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const runMerchandisingPreview = createAsyncThunk(
  'merchandising/runPreview',
  async ({ workspace, payload }, { extra, getState, rejectWithValue }) => {
    const compatibilityError = compatibilityErrorForRule(payload?.draft_rule, getState());
    if (compatibilityError) return rejectWithValue(compatibilityError);
    try {
      const api = getApi(extra);
      const response = workspace === 'query'
        ? await api.previewQuery(payload)
        : await api.previewIndexRule(payload);
      return { workspace, response };
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const startMerchandisingReindex = createAsyncThunk(
  'merchandising/startReindex',
  async (_, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    try {
      const indexingApi = getIndexingApi(extra);
      let response;
      if (typeof indexingApi.startCanonicalFull === 'function') {
        response = await indexingApi.startCanonicalFull();
      } else {
        if (typeof indexingApi.startFull !== 'function') {
          throw new Error('Current indexing transport cannot start a full index');
        }
        const feature = getState().merchandising;
        const shopId = feature.scopeShopId;
        if (!shopId) throw new Error('shopId is required to start indexing');
        const state = feature.indexState.value || await getApi(extra).getIndexState(null);
        const targetRevision = state.index_required_revision ?? state.config_revision;
        if (targetRevision === null || targetRevision === undefined) {
          throw new Error('The pending index revision is missing');
        }
        const published = revisionAtLeast(state.live_index_revision, targetRevision);
        if (!published) await indexingApi.startFull(shopId);
        response = {
          jobId: compatibilityFullJobId(shopId, targetRevision),
          indexing: {
            state: published ? 'completed' : 'queued',
            progress: published ? 100 : null,
            target_revision: String(targetRevision),
            live_revision: state.live_index_revision ?? state.live_revision ?? null,
          },
        };
      }
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const fetchMerchandisingReindexStatus = createAsyncThunk(
  'merchandising/fetchReindexStatus',
  async ({ jobId }, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    try {
      const compatibilityJob = parseCompatibilityFullJobId(jobId);
      let response;
      if (compatibilityJob) {
        const state = await getApi(extra).getIndexState(null);
        const publishedRevision = state.live_index_revision ?? state.live_revision ?? null;
        const published = revisionAtLeast(
          publishedRevision,
          compatibilityJob.targetRevision,
        );
        response = {
          jobId: String(jobId),
          state: published ? 'completed' : (state.dirty === false ? 'failed' : 'running'),
          progress: published ? 100 : null,
          error: !published && state.dirty === false
            ? 'The index completed without publishing the requested revision'
            : null,
          targetRevision: compatibilityJob.targetRevision,
          publishedRevision,
          indexing: state,
        };
      } else {
        const indexingApi = getIndexingApi(extra);
        if (typeof indexingApi.getStatus !== 'function') {
          throw new Error('Current indexing transport cannot read index status');
        }
        response = await indexingApi.getStatus(String(jobId));
      }
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const fetchMerchandisingIndexHistory = createAsyncThunk(
  'merchandising/fetchIndexHistory',
  async (_, {
    extra, getState, fulfillWithValue, rejectWithValue,
  }) => {
    const generation = scopeGeneration(getState);
    try {
      const response = await getIndexingApi(extra).getHistory(10);
      return fulfillInScope(response, generation, {
        getState, fulfillWithValue, rejectWithValue,
      });
    } catch (error) {
      return rejectInScope(errorPayload(error), generation, { getState, rejectWithValue });
    }
  },
);

export const fetchMerchandisingExplain = createAsyncThunk(
  'merchandising/fetchExplain',
  async ({ target, id, languageId = null }, { extra, rejectWithValue }) => {
    try {
      const api = getApi(extra);
      const value = target === 'product'
        ? await api.explainProduct({ languageId, productId: String(id) })
        : await api.explainCategory({ languageId, categoryId: String(id) });
      return { target, id: String(id), value };
    } catch (error) {
      return rejectWithValue(errorPayload(error));
    }
  },
);

export const openRuleEditor = (payload) => (dispatch) => {
  const recoveredDraft = payload.scope ? loadMerchandisingDraft(payload.scope) : null;
  dispatch(beginRuleEditor({ ...payload, recoveredDraft }));
};

export const updateRuleDraftAndPersist = (draft) => (dispatch, getState) => {
  dispatch(updateRuleDraft(draft));
  const scope = getState().merchandising.editor.scope;
  if (scope) saveMerchandisingDraft(scope, draft);
};

export const discardRuleDraftAndRecovery = () => (dispatch, getState) => {
  const scope = getState().merchandising.editor.scope;
  if (scope) clearMerchandisingDraft(scope);
  dispatch(discardRuleDraft());
};

const initialState = {
  scopeShopId: null,
  scopeGeneration: 0,
  capability: {
    root: false,
    rules: false,
    categories: false,
    preview: false,
    explain: false,
    queryGroups: false,
    csv: false,
    searchTerms: false,
    advancedConditions: false,
  },
  discovery: {
    languageId: null,
    schema: null,
    request: idleRequest(),
  },
  indexState: {
    languageId: null,
    value: null,
    request: idleRequest(),
  },
  rules: {
    entities: {},
    pages: {},
    activeFingerprint: null,
    filters: {},
    selectedIds: [],
    entityRequestIds: {},
    request: idleRequest(),
    lifecycleById: {},
  },
  queryGroups: {
    languageId: null,
    entities: {},
    order: [],
    nextCursor: null,
    current: null,
    selectedIds: [],
    request: idleRequest(),
    mutation: idleRequest(),
  },
  editor: {
    identity: null,
    scope: null,
    baseline: null,
    draft: null,
    dirty: false,
    validation: null,
    errorPaths: {},
    mutationId: null,
    mutationFingerprint: null,
    request: idleRequest(),
    recoveryAvailable: false,
  },
  categories: {
    entities: {},
    order: [],
    childrenByParent: {},
    expandedIds: [],
    selectedIds: [],
    filter: '',
    configRevision: null,
    indexState: null,
    pendingSets: {},
    pendingClears: {},
    recovery: null,
    request: idleRequest(),
  },
  previews: {
    query: { generation: 0, inputs: null, result: null, request: idleRequest() },
    index: { generation: 0, inputs: null, result: null, request: idleRequest() },
  },
  bulk: {
    active: false,
    operation: null,
    concurrency: 3,
    cancelRequested: false,
    items: [],
  },
  reindex: {
    jobId: null,
    status: null,
    history: [],
    request: idleRequest(),
  },
  explain: {
    target: null,
    id: null,
    value: null,
    request: idleRequest(),
  },
};

const setRequestPending = (request, action, hasValue) => {
  request.status = hasValue ? 'success' : 'loading';
  request.refreshing = hasValue;
  request.error = null;
  request.requestId = action.meta.requestId;
};

const setRequestFailed = (request, action) => {
  if (request.requestId !== action.meta.requestId) return;
  request.status = 'error';
  request.refreshing = false;
  request.error = action.payload || { message: action.error.message };
};

const queryGroupActionLanguage = (action) => action.meta.arg?.languageId ?? null;

const resetQueryGroupLanguageProjection = (queryGroups, languageId) => {
  if (queryGroups.languageId === languageId) return;
  queryGroups.languageId = languageId;
  queryGroups.entities = {};
  queryGroups.order = [];
  queryGroups.nextCursor = null;
  queryGroups.current = null;
  queryGroups.selectedIds = [];
  queryGroups.mutation = idleRequest();
};

const queryGroupActionMatchesLanguage = (state, action) => (
  state.queryGroups.languageId === queryGroupActionLanguage(action)
);

const upsertRule = (state, rule) => {
  if (!rule || rule.kind === 'category_priority') return;
  const id = String(rule.id);
  state.rules.entities[id] = { ...rule, id };
};

const merchandisingSlice = createSlice({
  name: 'merchandising',
  initialState,
  reducers: {
    setMerchandisingScopeShop(state, { payload }) {
      const scopeShopId = String(payload);
      if (state.scopeShopId === scopeShopId) return;
      return {
        ...initialState,
        scopeShopId,
        scopeGeneration: Number(state.scopeGeneration || 0) + 1,
      };
    },
    setMerchandisingCapabilities(state, { payload = {} }) {
      state.capability = {
        root: Boolean(payload.merchandising_bridge_v1),
        rules: Boolean(payload.merchandising_rules_v1),
        categories: Boolean(payload.category_priority_v2),
        preview: Boolean(payload.merchandising_preview_v1),
        explain: Boolean(payload.merchandising_explain_v1),
        queryGroups: Boolean(payload.merchandising_query_groups_v1),
        csv: Boolean(payload.merchandising_csv_v1),
        searchTerms: payload.merchandising_search_terms_v1 === true,
        advancedConditions: payload.merchandising_advanced_conditions_v1 === true,
      };
    },
    setMerchandisingQueryLanguage(state, { payload }) {
      resetQueryGroupLanguageProjection(state.queryGroups, payload ?? null);
    },
    setRuleFilters(state, { payload }) {
      const fingerprint = ruleFilterFingerprint(payload);
      if (fingerprint !== state.rules.activeFingerprint) state.rules.selectedIds = [];
      state.rules.filters = payload;
      state.rules.activeFingerprint = fingerprint;
    },
    toggleRuleSelection(state, { payload }) {
      const id = String(payload);
      const rule = state.rules.entities[id];
      if (!rule || rule.kind === 'category_priority') return;
      const position = state.rules.selectedIds.indexOf(id);
      if (position >= 0) state.rules.selectedIds.splice(position, 1);
      else state.rules.selectedIds.push(id);
    },
    clearRuleSelection(state) {
      state.rules.selectedIds = [];
    },
    toggleQueryGroupSelection(state, { payload }) {
      const id = String(payload);
      const position = state.queryGroups.selectedIds.indexOf(id);
      if (position >= 0) state.queryGroups.selectedIds.splice(position, 1);
      else state.queryGroups.selectedIds.push(id);
    },
    clearQueryGroupSelection(state) {
      state.queryGroups.selectedIds = [];
    },
    beginRuleEditor(state, { payload }) {
      const baseline = payload.rule || null;
      const recovered = payload.recoveredDraft || null;
      state.editor.identity = payload.identity;
      state.editor.scope = payload.scope || null;
      state.editor.baseline = baseline;
      state.editor.draft = recovered || baseline || payload.emptyDraft;
      state.editor.dirty = Boolean(recovered)
        || stableStringify(state.editor.draft) !== stableStringify(baseline);
      state.editor.recoveryAvailable = Boolean(recovered);
      state.editor.errorPaths = {};
      const fingerprint = stableStringify(state.editor.draft);
      state.editor.mutationFingerprint = fingerprint;
      state.editor.mutationId = uuid();
    },
    updateRuleDraft(state, { payload }) {
      state.editor.draft = payload;
      state.editor.dirty = stableStringify(payload) !== stableStringify(state.editor.baseline);
      state.editor.errorPaths = {};
      const fingerprint = stableStringify(payload);
      if (fingerprint !== state.editor.mutationFingerprint) {
        state.editor.mutationFingerprint = fingerprint;
        state.editor.mutationId = uuid();
      }
    },
    discardRuleDraft(state) {
      state.editor.draft = state.editor.baseline;
      state.editor.dirty = false;
      state.editor.recoveryAvailable = false;
      state.editor.errorPaths = {};
    },
    resolveRuleRevisionConflict(state, { payload }) {
      state.editor.baseline = payload.rule;
      if (!payload.keepDraft) state.editor.draft = payload.rule;
      state.editor.dirty = payload.keepDraft
        ? stableStringify(state.editor.draft) !== stableStringify(payload.rule)
        : false;
      state.editor.request = { ...idleRequest(), status: 'success' };
      state.editor.errorPaths = {};
    },
    setCategoryExpanded(state, { payload }) {
      const id = String(payload);
      const position = state.categories.expandedIds.indexOf(id);
      if (position >= 0) state.categories.expandedIds.splice(position, 1);
      else state.categories.expandedIds.push(id);
    },
    setCategoryFilter(state, { payload }) {
      state.categories.filter = payload;
    },
    setCategoryExpansion(state, { payload }) {
      state.categories.expandedIds = payload.map(String);
    },
    selectCategoryIds(state, { payload }) {
      const selected = new Set(state.categories.selectedIds);
      payload.map(String).forEach((id) => selected.add(id));
      state.categories.selectedIds = [...selected];
    },
    setCategorySelection(state, { payload }) {
      state.categories.selectedIds = [...new Set(payload.map(String))];
    },
    toggleCategorySelection(state, { payload }) {
      const id = String(payload);
      const position = state.categories.selectedIds.indexOf(id);
      if (position >= 0) state.categories.selectedIds.splice(position, 1);
      else state.categories.selectedIds.push(id);
    },
    clearCategorySelection(state) {
      state.categories.selectedIds = [];
    },
    stageCategoryMultiplier(state, { payload }) {
      const id = String(payload.categoryId);
      const row = state.categories.entities[id];
      if (!row) return;
      state.categories.pendingSets[id] = {
        category_id: id,
        multiplier: payload.multiplier,
        rule_id: row.manual_rule_id,
        revision: row.manual_rule_revision,
      };
      delete state.categories.pendingClears[id];
    },
    stageCategoryChanges(state, { payload }) {
      const changes = new Map((payload || []).map((change) => [String(change.categoryId), change.multiplier]));
      changes.forEach((multiplier, id) => {
        const row = state.categories.entities[id];
        if (!row) return;
        state.categories.pendingSets[id] = {
          category_id: id,
          multiplier,
          rule_id: row.manual_rule_id,
          revision: row.manual_rule_revision,
        };
        delete state.categories.pendingClears[id];
      });
    },
    stageCategoryClear(state, { payload }) {
      const id = String(payload);
      const row = state.categories.entities[id];
      if (!row || !row.manual_rule_id) return;
      state.categories.pendingClears[id] = {
        category_id: id,
        rule_id: row.manual_rule_id,
        revision: row.manual_rule_revision,
      };
      delete state.categories.pendingSets[id];
    },
    clearCategoryChanges(state) {
      state.categories.pendingSets = {};
      state.categories.pendingClears = {};
      state.categories.recovery = null;
    },
    discardCategoryChange(state, { payload }) {
      const id = String(payload);
      delete state.categories.pendingSets[id];
      delete state.categories.pendingClears[id];
    },
    clearPreview(state, { payload }) {
      state.previews[payload] = {
        generation: state.previews[payload].generation + 1,
        inputs: null,
        result: null,
        request: idleRequest(),
      };
    },
    clearQueryGroupEditorFeedback(state) {
      state.queryGroups.mutation = idleRequest();
      if (state.queryGroups.request.status === 'error') {
        state.queryGroups.request = idleRequest();
      }
    },
    clearRuleEditorFeedback(state) {
      if (state.editor.request.status === 'error') {
        state.editor.request = idleRequest();
      }
    },
    startBulkJob(state, { payload }) {
      if (state.bulk.active) return;
      state.bulk = {
        active: true,
        operation: payload.operation,
        concurrency: payload.concurrency || 3,
        cancelRequested: false,
        items: payload.items.map((item) => ({
          ...item,
          id: String(item.id),
          status: 'queued',
          error: null,
        })),
      };
    },
    updateBulkItem(state, { payload }) {
      const item = state.bulk.items.find((entry) => entry.id === String(payload.id));
      if (item) Object.assign(item, payload, { id: String(payload.id) });
      state.bulk.active = state.bulk.items.some((entry) =>
        entry.status === 'queued' || entry.status === 'running');
    },
    requestBulkCancel(state) {
      state.bulk.cancelRequested = true;
      state.bulk.items.forEach((item) => {
        if (item.status === 'queued') item.status = 'not-started';
      });
      state.bulk.active = state.bulk.items.some((item) => item.status === 'running');
    },
    resetMerchandisingState(state) {
      return {
        ...initialState,
        scopeGeneration: Number(state.scopeGeneration || 0) + 1,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMerchandisingSchema.pending, (state, action) => {
        setRequestPending(state.discovery.request, action, Boolean(state.discovery.schema));
        state.discovery.languageId = action.meta.arg.languageId ?? null;
      })
      .addCase(fetchMerchandisingSchema.fulfilled, (state, action) => {
        if (state.discovery.request.requestId !== action.meta.requestId) return;
        state.discovery.schema = action.payload;
        state.discovery.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(fetchMerchandisingSchema.rejected, (state, action) => {
        if (state.discovery.request.requestId !== action.meta.requestId) return;
        setRequestFailed(state.discovery.request, action);
      })
      .addCase(fetchMerchandisingIndexState.pending, (state, action) => {
        setRequestPending(state.indexState.request, action, Boolean(state.indexState.value));
        state.indexState.languageId = action.meta.arg.languageId ?? null;
      })
      .addCase(fetchMerchandisingIndexState.fulfilled, (state, action) => {
        if (state.indexState.request.requestId !== action.meta.requestId) return;
        state.indexState.value = action.payload;
        state.indexState.request = { ...idleRequest(), status: 'success' };
        const targetRevision = action.payload.index_required_revision
          ?? action.payload.config_revision;
        const liveRevision = action.payload.live_index_revision
          ?? action.payload.live_revision;
        const publicationCompleted = targetRevision == null
          ? (action.payload.dirty === false || action.payload.state === 'clean')
          : revisionAtLeast(liveRevision, targetRevision);
        // A full-index request can outlive the browser request that started it.
        // Once the canonical backend confirms publication, an old transport
        // error must not leave the drawer permanently failed until a reload.
        if (publicationCompleted && state.reindex.request.status === 'error') {
          state.reindex.request = { ...idleRequest(), status: 'success' };
        }
      })
      .addCase(fetchMerchandisingIndexState.rejected, (state, action) => {
        if (state.indexState.request.requestId !== action.meta.requestId) return;
        setRequestFailed(state.indexState.request, action);
      })
      .addCase(fetchMerchandisingRules.pending, (state, action) => {
        setRequestPending(state.rules.request, action, Boolean(state.rules.pages[state.rules.activeFingerprint]));
      })
      .addCase(fetchMerchandisingRules.fulfilled, (state, action) => {
        if (state.rules.request.requestId !== action.meta.requestId) return;
        const {
          response, fingerprint, append, filters,
        } = action.payload;
        response.items.forEach((rule) => upsertRule(state, rule));
        const safeIds = response.items
          .filter((rule) => rule.kind !== 'category_priority')
          .map((rule) => String(rule.id));
        const previous = state.rules.pages[fingerprint]?.ids || [];
        state.rules.pages[fingerprint] = {
          ids: append ? [...previous, ...safeIds] : safeIds,
          nextCursor: response.next_cursor ?? null,
          configRevision: response.config_revision ?? response.meta?.config_revision ?? null,
          filters,
        };
        state.rules.activeFingerprint = fingerprint;
        state.rules.filters = filters;
        state.rules.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(fetchMerchandisingRules.rejected, (state, action) => {
        if (state.rules.request.requestId !== action.meta.requestId) return;
        setRequestFailed(state.rules.request, action);
      })
      .addCase(fetchMerchandisingRule.pending, (state, action) => {
        state.rules.entityRequestIds[String(action.meta.arg.id)] = action.meta.requestId;
      })
      .addCase(fetchMerchandisingRule.fulfilled, (state, action) => {
        const id = String(action.meta.arg.id);
        if (state.rules.entityRequestIds[id] !== action.meta.requestId) return;
        delete state.rules.entityRequestIds[id];
        upsertRule(state, action.payload);
      })
      .addCase(fetchMerchandisingRule.rejected, (state, action) => {
        const id = String(action.meta.arg.id);
        if (state.rules.entityRequestIds[id] === action.meta.requestId) {
          delete state.rules.entityRequestIds[id];
        }
      })
      .addCase(fetchMerchandisingQueryGroups.pending, (state, action) => {
        const languageId = queryGroupActionLanguage(action);
        resetQueryGroupLanguageProjection(state.queryGroups, languageId);
        setRequestPending(state.queryGroups.request, action, state.queryGroups.order.length > 0);
      })
      .addCase(fetchMerchandisingQueryGroups.fulfilled, (state, action) => {
        if (state.queryGroups.request.requestId !== action.meta.requestId) return;
        const items = action.payload.items || [];
        items.forEach((group) => {
          state.queryGroups.entities[String(group.id)] = { ...group, id: String(group.id) };
        });
        const ids = items.map((group) => String(group.id));
        state.queryGroups.order = action.meta.arg?.cursor
          ? Array.from(new Set([...state.queryGroups.order, ...ids]))
          : ids;
        state.queryGroups.nextCursor = action.payload.next_cursor || null;
        state.queryGroups.selectedIds = state.queryGroups.selectedIds
          .filter((id) => state.queryGroups.order.includes(id));
        state.queryGroups.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(fetchMerchandisingQueryGroups.rejected, (state, action) => {
        if (state.queryGroups.request.requestId !== action.meta.requestId) return;
        setRequestFailed(state.queryGroups.request, action);
      })
      .addCase(fetchMerchandisingQueryGroup.pending, (state, action) => {
        const languageId = queryGroupActionLanguage(action);
        resetQueryGroupLanguageProjection(state.queryGroups, languageId);
        setRequestPending(state.queryGroups.request, action, Boolean(state.queryGroups.current));
      })
      .addCase(fetchMerchandisingQueryGroup.fulfilled, (state, action) => {
        if (state.queryGroups.request.requestId !== action.meta.requestId) return;
        const value = action.payload;
        state.queryGroups.current = value;
        if (value.group) {
          state.queryGroups.entities[String(value.group.id)] = {
            ...value.group,
            id: String(value.group.id),
          };
        }
        state.queryGroups.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(fetchMerchandisingQueryGroup.rejected, (state, action) => {
        if (state.queryGroups.request.requestId !== action.meta.requestId) return;
        setRequestFailed(state.queryGroups.request, action);
      })
      .addCase(saveMerchandisingQueryGroup.pending, (state, action) =>
        setRequestPending(state.queryGroups.mutation, action, false))
      .addCase(saveMerchandisingQueryGroup.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action) || !queryGroupActionMatchesLanguage(state, action)) return;
        state.queryGroups.current = action.payload;
        const group = action.payload.group;
        state.queryGroups.entities[String(group.id)] = { ...group, id: String(group.id) };
        if (!state.queryGroups.order.includes(String(group.id))) {
          state.queryGroups.order.unshift(String(group.id));
        }
        state.queryGroups.mutation = { ...idleRequest(), status: 'success' };
      })
      .addCase(saveMerchandisingQueryGroup.rejected, (state, action) => {
        if (!actionMatchesScope(state, action) || !queryGroupActionMatchesLanguage(state, action)) return;
        setRequestFailed(state.queryGroups.mutation, action);
      })
      .addCase(changeMerchandisingQueryGroupStatus.pending, (state, action) =>
        setRequestPending(state.queryGroups.mutation, action, false))
      .addCase(changeMerchandisingQueryGroupStatus.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action) || !queryGroupActionMatchesLanguage(state, action)) return;
        const group = action.payload.group;
        state.queryGroups.entities[String(group.id)] = { ...group, id: String(group.id) };
        if (state.queryGroups.current?.group?.id === group.id) {
          state.queryGroups.current = action.payload;
        }
        state.queryGroups.mutation = { ...idleRequest(), status: 'success' };
      })
      .addCase(changeMerchandisingQueryGroupStatus.rejected, (state, action) => {
        if (!actionMatchesScope(state, action) || !queryGroupActionMatchesLanguage(state, action)) return;
        setRequestFailed(state.queryGroups.mutation, action);
      })
      .addCase(deleteMerchandisingQueryGroup.pending, (state, action) =>
        setRequestPending(state.queryGroups.mutation, action, false))
      .addCase(deleteMerchandisingQueryGroup.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action) || !queryGroupActionMatchesLanguage(state, action)) return;
        const id = String(action.payload.id);
        delete state.queryGroups.entities[id];
        state.queryGroups.order = state.queryGroups.order.filter((groupId) => groupId !== id);
        state.queryGroups.selectedIds = state.queryGroups.selectedIds.filter((groupId) => groupId !== id);
        if (String(state.queryGroups.current?.group?.id || '') === id) {
          state.queryGroups.current = null;
        }
        state.queryGroups.mutation = { ...idleRequest(), status: 'success' };
      })
      .addCase(deleteMerchandisingQueryGroup.rejected, (state, action) => {
        if (!actionMatchesScope(state, action) || !queryGroupActionMatchesLanguage(state, action)) return;
        setRequestFailed(state.queryGroups.mutation, action);
      })
      .addCase(validateMerchandisingRule.pending, (state, action) => {
        setRequestPending(state.editor.request, action, false);
        state.editor.validation = null;
      })
      .addCase(validateMerchandisingRule.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        state.editor.validation = action.payload;
        state.editor.errorPaths = {};
        state.editor.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(validateMerchandisingRule.rejected, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        setRequestFailed(state.editor.request, action);
        state.editor.errorPaths = Object.fromEntries(
          (action.payload?.details || []).map((detail) => [detail.path, detail.reason]),
        );
      })
      .addCase(saveMerchandisingRule.pending, (state, action) =>
        setRequestPending(state.editor.request, action, false))
      .addCase(saveMerchandisingRule.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        const rule = action.payload.rule || action.payload;
        upsertRule(state, rule);
        state.editor.baseline = rule;
        state.editor.draft = rule;
        state.editor.dirty = false;
        state.editor.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(saveMerchandisingRule.rejected, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        setRequestFailed(state.editor.request, action);
      })
      .addCase(changeMerchandisingRuleStatus.pending, (state, action) => {
        state.rules.lifecycleById[String(action.meta.arg.id)] = {
          status: 'loading', error: null,
        };
      })
      .addCase(changeMerchandisingRuleStatus.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        const rule = action.payload.rule || action.payload;
        upsertRule(state, rule);
        state.rules.lifecycleById[String(rule.id)] = { status: 'success', error: null };
        if (String(state.editor.baseline?.id || '') === String(rule.id)
          && !state.editor.dirty) {
          state.editor.baseline = rule;
          state.editor.draft = rule;
        }
      })
      .addCase(changeMerchandisingRuleStatus.rejected, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        state.rules.lifecycleById[String(action.meta.arg.id)] = {
          status: 'error', error: action.payload,
        };
      })
      .addCase(archiveMerchandisingRule.pending, (state, action) => {
        state.rules.lifecycleById[String(action.meta.arg.id)] = {
          status: 'loading', error: null,
        };
      })
      .addCase(archiveMerchandisingRule.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        const rule = action.payload.rule || action.payload;
        upsertRule(state, rule);
        state.rules.lifecycleById[String(rule.id)] = { status: 'success', error: null };
        if (String(state.editor.baseline?.id || '') === String(rule.id)
          && !state.editor.dirty) {
          state.editor.baseline = rule;
          state.editor.draft = rule;
        }
      })
      .addCase(archiveMerchandisingRule.rejected, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        state.rules.lifecycleById[String(action.meta.arg.id)] = {
          status: 'error', error: action.payload,
        };
      })
      .addCase(deleteMerchandisingRule.pending, (state, action) => {
        state.rules.lifecycleById[String(action.meta.arg.id)] = {
          status: 'loading', error: null,
        };
      })
      .addCase(deleteMerchandisingRule.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        const id = String(action.payload.id);
        delete state.rules.entities[id];
        Object.values(state.rules.pages).forEach((page) => {
          page.ids = page.ids.filter((ruleId) => String(ruleId) !== id);
        });
        state.rules.lifecycleById[id] = { status: 'success', error: null };
        if (String(state.editor.baseline?.id || '') === id) {
          state.editor.baseline = null;
          state.editor.draft = null;
          state.editor.dirty = false;
        }
      })
      .addCase(deleteMerchandisingRule.rejected, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        state.rules.lifecycleById[String(action.meta.arg.id)] = {
          status: 'error', error: action.payload,
        };
      })
      .addCase(fetchMerchandisingCategories.pending, (state, action) =>
        setRequestPending(state.categories.request, action, state.categories.order.length > 0))
      .addCase(fetchMerchandisingCategories.fulfilled, (state, action) => {
        if (state.categories.request.requestId !== action.meta.requestId) return;
        const initializeExpansion = state.categories.order.length === 0
          && state.categories.expandedIds.length === 0;
        const entities = {};
        const childrenByParent = {};
        const order = [];
        action.payload.items.forEach((item) => {
          const id = String(item.id);
          const parentId = item.parent_id == null ? '__root__' : String(item.parent_id);
          entities[id] = { ...item, id, parent_id: item.parent_id == null ? null : parentId };
          order.push(id);
          if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
          childrenByParent[parentId].push(id);
        });
        order.forEach((id) => {
          const row = entities[id];
          if (row.parent_id == null || entities[row.parent_id]) return;
          const missingParent = row.parent_id;
          childrenByParent[missingParent] = (childrenByParent[missingParent] || [])
            .filter((childId) => childId !== id);
          if (!childrenByParent.__root__) childrenByParent.__root__ = [];
          childrenByParent.__root__.push(id);
          row.parent_id = null;
          row.broken_parent_id = missingParent;
        });
        state.categories.entities = entities;
        state.categories.order = order;
        state.categories.childrenByParent = childrenByParent;
        if (initializeExpansion) {
          state.categories.expandedIds = action.payload.items
            .filter((item) => Number(item.depth) < 2)
            .map((item) => String(item.id));
        }
        state.categories.configRevision = action.payload.config_revision;
        state.categories.indexState = action.payload.index_state;
        state.categories.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(fetchMerchandisingCategories.rejected, (state, action) => {
        if (state.categories.request.requestId !== action.meta.requestId) return;
        setRequestFailed(state.categories.request, action);
      })
      .addCase(saveMerchandisingCategoryChanges.pending, (state, action) => {
        state.categories.recovery = null;
        setRequestPending(state.categories.request, action, true);
      })
      .addCase(saveMerchandisingCategoryChanges.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        state.categories.pendingSets = {};
        state.categories.pendingClears = {};
        state.categories.recovery = null;
        state.categories.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(saveMerchandisingCategoryChanges.rejected, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        const partial = action.payload?.partialResult;
        (partial?.appliedSetIds || []).forEach((id) => {
          delete state.categories.pendingSets[String(id)];
        });
        (partial?.appliedClearIds || []).forEach((id) => {
          delete state.categories.pendingClears[String(id)];
        });
        if (partial?.projection?.items) {
          partial.projection.items.forEach((row) => {
            const id = String(row.id);
            if (state.categories.entities[id]) {
              state.categories.entities[id] = {
                ...row,
                id,
                parent_id: row.parent_id == null ? null : String(row.parent_id),
              };
            }
            if (state.categories.pendingSets[id]) {
              state.categories.pendingSets[id].rule_id = row.manual_rule_id;
              state.categories.pendingSets[id].revision = row.manual_rule_revision;
            }
            if (state.categories.pendingClears[id]) {
              state.categories.pendingClears[id].rule_id = row.manual_rule_id;
              state.categories.pendingClears[id].revision = row.manual_rule_revision;
            }
          });
          state.categories.configRevision = partial.projection.config_revision;
          state.categories.indexState = partial.projection.index_state;
        }
        const pendingIds = [
          ...Object.keys(state.categories.pendingSets),
          ...Object.keys(state.categories.pendingClears),
        ];
        state.categories.recovery = {
          recoveredIds: [
            ...(partial?.appliedSetIds || []),
            ...(partial?.appliedClearIds || []),
          ],
          conflictIds: pendingIds,
        };
        if (pendingIds.length === 0 && state.categories.recovery.recoveredIds.length > 0) {
          state.categories.request = { ...idleRequest(), status: 'success' };
        } else {
          setRequestFailed(state.categories.request, action);
        }
      })
      .addCase(runMerchandisingPreview.pending, (state, action) => {
        const preview = state.previews[action.meta.arg.workspace];
        preview.generation += 1;
        preview.inputs = action.meta.arg.payload;
        setRequestPending(preview.request, action, Boolean(preview.result));
      })
      .addCase(runMerchandisingPreview.fulfilled, (state, action) => {
        const preview = state.previews[action.payload.workspace];
        if (preview.request.requestId !== action.meta.requestId) return;
        preview.result = action.payload.response;
        preview.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(runMerchandisingPreview.rejected, (state, action) => {
        const preview = state.previews[action.meta.arg.workspace];
        if (preview.request.requestId !== action.meta.requestId) return;
        setRequestFailed(preview.request, action);
      })
      .addCase(startMerchandisingReindex.pending, (state, action) =>
        setRequestPending(state.reindex.request, action, Boolean(state.reindex.status)))
      .addCase(startMerchandisingReindex.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        state.reindex.jobId = action.payload.jobId;
        state.reindex.status = action.payload.indexing;
        state.reindex.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(startMerchandisingReindex.rejected, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        setRequestFailed(state.reindex.request, action);
      })
      .addCase(fetchMerchandisingReindexStatus.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        state.reindex.status = action.payload;
        state.reindex.jobId = action.payload.jobId;
      })
      .addCase(fetchMerchandisingIndexHistory.fulfilled, (state, action) => {
        if (!actionMatchesScope(state, action)) return;
        state.reindex.history = action.payload.history || [];
      })
      .addCase(fetchMerchandisingExplain.pending, (state, action) => {
        state.explain.target = action.meta.arg.target;
        state.explain.id = String(action.meta.arg.id);
        state.explain.value = null;
        setRequestPending(state.explain.request, action, false);
      })
      .addCase(fetchMerchandisingExplain.fulfilled, (state, action) => {
        if (state.explain.request.requestId !== action.meta.requestId) return;
        state.explain.value = action.payload.value;
        state.explain.request = { ...idleRequest(), status: 'success' };
      })
      .addCase(fetchMerchandisingExplain.rejected, (state, action) => {
        if (state.explain.request.requestId !== action.meta.requestId) return;
        setRequestFailed(state.explain.request, action);
      });
  },
});

export const {
  beginRuleEditor,
  clearCategoryChanges,
  clearCategorySelection,
  clearPreview,
  clearQueryGroupEditorFeedback,
  clearRuleEditorFeedback,
  clearRuleSelection,
  clearQueryGroupSelection,
  discardCategoryChange,
  discardRuleDraft,
  requestBulkCancel,
  resetMerchandisingState,
  setMerchandisingScopeShop,
  resolveRuleRevisionConflict,
  selectCategoryIds,
  setCategorySelection,
  setCategoryExpanded,
  setCategoryExpansion,
  setCategoryFilter,
  setMerchandisingCapabilities,
  setMerchandisingQueryLanguage,
  setRuleFilters,
  stageCategoryClear,
  stageCategoryChanges,
  stageCategoryMultiplier,
  startBulkJob,
  toggleCategorySelection,
  toggleRuleSelection,
  toggleQueryGroupSelection,
  updateBulkItem,
  updateRuleDraft,
} = merchandisingSlice.actions;

export const selectMerchandising = (state) => state.merchandising;
export const selectActiveRulePage = (state) => {
  const feature = state.merchandising;
  const page = feature.rules.pages[feature.rules.activeFingerprint] || {
    ids: [], nextCursor: null,
  };
  return {
    ...page,
    items: page.ids.map((id) => feature.rules.entities[id]).filter(Boolean),
  };
};
export const selectCategoryRows = (state) =>
  state.merchandising.categories.order.map((id) =>
    state.merchandising.categories.entities[id]).filter(Boolean);
export const selectBulkProgress = (state) => {
  const items = state.merchandising.bulk.items;
  const completed = items.filter((item) =>
    ['succeeded', 'conflicted', 'failed', 'skipped', 'not-started'].includes(item.status)).length;
  return { completed, total: items.length };
};
export const selectRetryableBulkItems = (state) =>
  state.merchandising.bulk.items.filter((item) =>
    ['conflicted', 'failed', 'not-started'].includes(item.status));

export default merchandisingSlice.reducer;
